import { useCallback, useContext, useMemo } from 'react';

import { Variable, ComponentType, VariantNode } from '@candu-ai/styleguide/types';

import { UpdateVariantsRules, StyleguideContext } from './StyleguideContext';

export const useVariant = (componentType: ComponentType, selectedVariant: string) => {
  const { variants, variables, updateRules, updateName } = useContext(StyleguideContext);
  const changeValue = useCallback(
    async (rule: Variable, payload: UpdateVariantsRules['items']) => {
      await updateRules?.({
        componentType,
        variant: selectedVariant,
        rule,
        items: payload,
      });
    },
    [selectedVariant],
  );

  const changeName = useCallback(
    async (updatedName: string) => {
      await updateName?.({
        componentType,
        variant: selectedVariant,
        name: updatedName,
      });
    },
    [selectedVariant],
  );

  const variant = useMemo(() => variants?.[componentType][selectedVariant], [
    componentType,
    selectedVariant,
    variants,
  ]);

  const lastOfType = useMemo(() => {
    const variantsForType = (variants?.[componentType] || {}) as Record<string, VariantNode>;
    const visibleEntries = Object.values(variantsForType).filter((v) => !v.isHidden);

    return visibleEntries.length === 1;
  }, [componentType, variants]);

  return {
    changeValue,
    changeName,
    variant,
    variables,
    lastOfType,
  };
};
